import { Card } from "@/components/ui/card";
import { Activity, CheckCircle2 } from "lucide-react";
import type { NHSHypertensionABPMFittingFormData } from "./NHSHypertensionABPMFittingForm";

interface Props {
  isDarkMode?: boolean;
  data: NHSHypertensionABPMFittingFormData;
}

const formatDate = (s: string) =>
  s
    ? new Date(s).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "Not specified";

const formatDateTime = (s: string) => {
  if (!s) return "Not specified";
  const d = new Date(s);
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export function NHSHypertensionABPMFittingSummary({
  isDarkMode = false,
  data,
}: Props) {
  const cardCls = `p-4 rounded-2xl border ${
    isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
  }`;
  const titleCls = `text-sm font-semibold ${
    isDarkMode ? "text-white" : "text-gray-900"
  }`;
  const labelCls = `text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`;
  const valueCls = `text-sm ${isDarkMode ? "text-gray-100" : "text-gray-900"}`;

  return (
    <div className="space-y-4">
      <div
        className={`flex items-start gap-3 p-4 rounded-2xl ${
          isDarkMode
            ? "bg-emerald-900/20 border border-emerald-800"
            : "bg-emerald-50 border border-emerald-200"
        }`}
      >
        <CheckCircle2
          className={`w-5 h-5 mt-0.5 ${
            isDarkMode ? "text-emerald-300" : "text-emerald-600"
          }`}
        />
        <div>
          <div
            className={`text-sm font-semibold ${
              isDarkMode ? "text-emerald-200" : "text-emerald-900"
            }`}
          >
            Stage 2 – ABPM Fitting Completed
          </div>
          <div className={labelCls}>
            {formatDate(data.consultationDate)} · {data.consultationTime || "—"}
          </div>
        </div>
      </div>

      <Card className={cardCls}>
        <div className="flex items-center gap-2 mb-3">
          <Activity
            className={`w-4 h-4 ${
              isDarkMode ? "text-rose-300" : "text-rose-600"
            }`}
          />
          <h4 className={titleCls}>Device</h4>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className={labelCls}>Make / model</div>
            <div className={valueCls}>{data.deviceMakeModel || "—"}</div>
          </div>
          <div>
            <div className={labelCls}>Serial number</div>
            <div className={valueCls}>{data.deviceSerialNumber || "—"}</div>
          </div>
          <div>
            <div className={labelCls}>Cuff size</div>
            <div className={valueCls}>{data.cuffSize || "—"}</div>
          </div>
          <div>
            <div className={labelCls}>Arm</div>
            <div className={valueCls}>{data.armUsed || "—"}</div>
          </div>
          <div>
            <div className={labelCls}>Test reading</div>
            <div className={valueCls}>
              {data.testReadingSystolic && data.testReadingDiastolic
                ? `${data.testReadingSystolic}/${data.testReadingDiastolic} mmHg`
                : "—"}
            </div>
          </div>
        </div>
      </Card>

      <Card className={cardCls}>
        <h4 className={titleCls}>Monitoring Programme</h4>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div>
            <div className={labelCls}>Start</div>
            <div className={valueCls}>
              {formatDateTime(data.monitoringStart)}
            </div>
          </div>
          <div>
            <div className={labelCls}>End</div>
            <div className={valueCls}>{formatDateTime(data.monitoringEnd)}</div>
          </div>
          <div>
            <div className={labelCls}>Daytime frequency</div>
            <div className={valueCls}>
              {data.daytimeFrequency
                ? `Every ${data.daytimeFrequency} min`
                : "—"}
            </div>
          </div>
          <div>
            <div className={labelCls}>Night-time frequency</div>
            <div className={valueCls}>
              {data.nighttimeFrequency
                ? `Every ${data.nighttimeFrequency} min`
                : "—"}
            </div>
          </div>
        </div>
      </Card>

      <Card className={cardCls}>
        <h4 className={titleCls}>Patient Education</h4>
        <ul className={`${valueCls} mt-2 space-y-1 list-disc pl-5`}>
          <li>Diary provided: {data.diaryProvided ? "Yes" : "No"}</li>
          <li>
            Removal instructions:{" "}
            {data.removalInstructionsGiven ? "Given" : "Not given"}
          </li>
          <li>
            Emergency contact:{" "}
            {data.emergencyContactGiven ? "Given" : "Not given"}
          </li>
          <li>
            Patient confirmed understanding:{" "}
            {data.patientUnderstandsCare || "—"}
          </li>
        </ul>
      </Card>

      <Card className={cardCls}>
        <h4 className={titleCls}>Stage 3 Follow-Up Scheduled</h4>
        <div className="mt-3 space-y-2">
          <div>
            <div className={labelCls}>Follow-up date</div>
            <div className={valueCls}>{formatDate(data.followUpDate)}</div>
          </div>
          {data.followUpNotes && (
            <div>
              <div className={labelCls}>Notes</div>
              <div className={valueCls}>{data.followUpNotes}</div>
            </div>
          )}
        </div>
      </Card>

      {data.internalNotes && (
        <Card className={cardCls}>
          <h4 className={titleCls}>Internal Notes</h4>
          <div className={`${valueCls} mt-2 whitespace-pre-wrap`}>
            {data.internalNotes}
          </div>
        </Card>
      )}
    </div>
  );
}
